import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { Lock, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import './Auth.css';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidating(false);
      setTokenValid(false);
      return;
    }

    authAPI.validateResetToken(token)
      .then(() => {
        setTokenValid(true);
        setValidating(false);
      })
      .catch(() => {
        setTokenValid(false);
        setValidating(false);
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await authAPI.resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="auth-container">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <h2>Validating Reset Link...</h2>
          <p className="reset-info-text">Please wait while we verify your reset token.</p>
        </div>
      </div>
    );
  }

  if (!token || !tokenValid) {
    return (
      <div className="auth-container">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <div className="reset-success-icon">
            <AlertCircle size={48} color="#ef4444" />
          </div>
          <h2>Invalid or Expired Link</h2>
          <p className="reset-info-text">
            This password reset link is invalid or has expired. Please request a new one.
          </p>
          <Link to="/forgot-password" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none', marginTop: 16, padding: '12px 32px' }}>
            Request New Link
          </Link>
          <p className="auth-link" style={{ marginTop: 20 }}>
            <Link to="/login"><ArrowLeft size={14} style={{ verticalAlign: 'middle' }} /> Back to Login</Link>
          </p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <div className="reset-success-icon">
            <CheckCircle size={48} color="#22c55e" />
          </div>
          <h2>Password Reset!</h2>
          <p className="reset-info-text">
            Your password has been successfully changed. You can now log in with your new password.
          </p>
          <button
            className="btn-primary"
            style={{ marginTop: 16 }}
            onClick={() => navigate('/login')}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>Brand Ambassador Platform</h1>
        <h2>Set New Password</h2>
        <p className="reset-info-text">
          Enter your new password below. It must be at least 8 characters long.
        </p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>New Password</label>
            <div className="input-icon-wrapper">
              <Lock size={18} className="input-icon" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                required
                minLength={8}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Confirm New Password</label>
            <div className="input-icon-wrapper">
              <Lock size={18} className="input-icon" />
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
                minLength={8}
              />
            </div>
          </div>

          {password && confirmPassword && password !== confirmPassword && (
            <p className="password-mismatch">Passwords do not match</p>
          )}

          {password && password.length >= 8 && (
            <div className="password-strength">
              <div className={`strength-bar ${password.length >= 12 ? 'strong' : password.length >= 8 ? 'medium' : 'weak'}`}></div>
              <span className="strength-label">
                {password.length >= 12 ? 'Strong' : password.length >= 8 ? 'Good' : 'Weak'}
              </span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || password !== confirmPassword || password.length < 8}
            className="btn-primary"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <p className="auth-link" style={{ marginTop: 20 }}>
          <Link to="/login"><ArrowLeft size={14} style={{ verticalAlign: 'middle' }} /> Back to Login</Link>
        </p>
      </div>
    </div>
  );
}

export default ResetPassword;
